import React, { useState, useEffect } from "react";
import { getFromLocalStorage } from "../../../utils/common-utils";
import { findCustomerCreDetails, GetCountries } from "../../../api/Api"; // Import your API functions
import Swal from "sweetalert2";
import CustomTable from "../../../components/Table/CustomTable";
import ViewCustomerCare from "./viewCustomerCare/viewCustomerCare";
import CustomModal from "../../../components/Modal/CustomModal";
import { useNavigate, useLocation } from "react-router-dom";
import "./CustomerCare.css";


export default function CustomerCare() {
  const [countryList, setCountryList] = useState([]);
    const [pageSize, setPageSize] = useState(10);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const saved = localStorage.getItem('customerCare_country');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedOperator, setSelectedOperator] = useState(() => {
    const saved = localStorage.getItem('customerCare_operator');
    return saved ? JSON.parse(saved) : null;
  });
  const [phoneNumber, setPhoneNumber] = useState(() => {
    return localStorage.getItem('customerCare_phone') || "";
  });
  const [isActive, setIsActive] = useState(() => {
    const saved = localStorage.getItem('customerCare_isActive');
    return saved ? JSON.parse(saved) : true;
  });
  const [viewCpModal, setViewCpModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowData, setRowData] = useState(null);
  // const [pageSize] = useState(10);

  // Add loading state  
  const [loaders, setLoaders] = useState({
    countryLoader: false,
  });

  // Add these new states after your existing states
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [tableConfig, setTableConfig] = useState({
    enabledColumns: [],
    actions: [],
  });
  const [paginationData, setPaginationData] = useState({
    totalElements: 0,
    totalPages: 0
  });
  const navigate = useNavigate();
  // Fetch countries on component mount
  useEffect(() => {
    getCountriesAndOperators();
  }, []);

  // Add this useEffect to save form state when it changes
  useEffect(() => {
    if (selectedCountry) {
      localStorage.setItem('customerCare_country', JSON.stringify(selectedCountry));
    }
    if (selectedOperator) {
      localStorage.setItem('customerCare_operator', JSON.stringify(selectedOperator));
    }
    if (phoneNumber) {
      localStorage.setItem('customerCare_phone', phoneNumber);
    }
    localStorage.setItem('customerCare_isActive', JSON.stringify(isActive));
  }, [selectedCountry, selectedOperator, phoneNumber, isActive]);

  const getCountriesAndOperators = async () => {
    setLoaders((prev) => ({ ...prev, countryLoader: true }));
    try {
      const response = await GetCountries();
      if (response?.statusDescription?.status === 200) {
        setCountryList(response?.countryResponseList);
      } else {
        setCountryList([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountryList([]);
    }
    setLoaders((prev) => ({ ...prev, countryLoader: false }));
  };

  const getRequiredLength = () => {
    if (!selectedCountry?.msisdnLength || !selectedCountry?.msisdnPrefix) return 0;
    return selectedCountry.msisdnLength - selectedCountry.msisdnPrefix.length;
  };

  const handleCountryChange = (e) => {
    const country = countryList.find((c) => c.countryName === e.target.value);
    setSelectedCountry(country);

    // If there's a selected operator, try to find it in the new country's operator list
    if (selectedOperator) {
      const operatorExists = country?.operatorList?.find(
        op => op.operatorName === selectedOperator.operatorName
      );

      // Only reset operator if it doesn't exist in new country's list
      if (!operatorExists) {
        setSelectedOperator(null);
      }
    }

    // Don't reset phone number unless country prefix changes
    if (country?.msisdnPrefix !== selectedCountry?.msisdnPrefix) {
      setPhoneNumber("");
    }
  };

  const handleOperatorChange = (e) => {
    const operator = selectedCountry?.operatorList.find(
      (op) => op.operatorName === e.target.value
    );
    setSelectedOperator(operator);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCountry || !selectedOperator || !phoneNumber) {
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: "Please fill all required fields",
      });
      return;
    }

    if (phoneNumber.length !== getRequiredLength()) {
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: `Please enter ${getRequiredLength()} digits after +${selectedCountry.msisdnPrefix}`,
      });
      return;
    }

    try {
      const payload = {
        userId: getFromLocalStorage("userData")?.id,
        msisdn: selectedCountry.msisdnPrefix + phoneNumber,
        pageNo: currentPage,
        pageSize: pageSize,
        operatorCode: selectedOperator?.opUniId,
        countryCode: selectedCountry?.cnUniId,
        isActive: isActive
      };

      const response = await findCustomerCreDetails(payload);

      if (response?.statusDescription?.status === 200) {
        // Map the subscription data for the table
        const tableData = response.appstoreUserSubscription.content.map((item) => ({
          ...item,
          actions: response.action // Add actions to each row
        }));

        setSubscriptionData(tableData);
        setTableConfig({
          enabledColumns: response.enabledColumns,
          actions: response.action,
        });
        setPaginationData({
          totalElements: response.appstoreUserSubscription.totalElements,
          totalPages: response.appstoreUserSubscription.totalPages
        });
      } else if (response?.statusDescription?.status === 210) {
        setSubscriptionData([]);
        Swal.fire({
          icon: "info",
          title: "No Data",
          text: response?.statusDescription?.description || "No data found",
        });
      } else {
        throw new Error(response?.statusDescription?.description || "Failed to fetch details");
      }
    } catch (error) {
      console.error("Error:", error);
      setSubscriptionData([]);
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: error.message || "Failed to fetch customer details",
      });
    }
  };

  // Update the handleTableAction function
  const handleAction = (action, rowData, newStatus) => {
    console.log("rowData", rowData, action);
    setRowData(rowData);
    if (action.sequence === 2) {
      setViewCpModal(true);
    } else if (action?.sequence === 7) {
      // Store the current form state before navigation
      localStorage.setItem('customerCare_country', JSON.stringify(selectedCountry));
      localStorage.setItem('customerCare_operator', JSON.stringify(selectedOperator));
      localStorage.setItem('customerCare_phone', phoneNumber);
      localStorage.setItem('customerCare_isActive', JSON.stringify(isActive));

      navigate("/customer-care/customer-details/sub", {
        state: rowData,
      });
    }
  };

  // Update the renderActionButtons function
  const renderActionButtons = (actions) => {
    return actions.map((action) => (
      <button
        key={action.id}
        className={`btn btn-link ${action.colorCode}`}
        title={action.titleName}
        onClick={() => handleAction(action)}
      >
        <i className={action.iconName}></i>
      </button>
    ));
  };

  // Add a cleanup function when component unmounts
  useEffect(() => {
    return () => {
      // Only clear the storage if we're not navigating to the sub route
      if (!window.location.pathname.includes('/customer-care/customer-details/sub')) {
        localStorage.removeItem('customerCare_country');
        localStorage.removeItem('customerCare_operator');
        localStorage.removeItem('customerCare_phone');
        localStorage.removeItem('customerCare_isActive');
      }
    };
  }, []);

  // Add this useEffect to fetch data on mount if we have stored values
  useEffect(() => {
    if (selectedCountry && selectedOperator && phoneNumber) {
      handleSubmit({ preventDefault: () => { } });
    }
  }, []); // Empty dependency array means this runs once on mount

  return (
    <div className="content-wrapper">
      <section className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="box">
              <div className="box-header mt-0 pt-0">
                <div className="row mb-0 pb-0">
                  <div className="col-md-12 pt-2 pt-md-0">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb breadcrumb_bg">
                        <li className="breadcrumb-item">
                          <a className="breadcrumb_1">Customer Care</a>
                        </li>
                        <li
                          className="breadcrumb-item active breadcrumb_2 d-flex align-items-center"
                          aria-current="page"
                        >
                         <span>Customer Details</span> 
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>
              </div>
              <div className="box-body pt-0 mt-0">
                <form onSubmit={handleSubmit}>
                  <div className="card mt-4 position-relative">
                    <span className="mt-2 mb-3 fw-bold badge_head text-center">Customer Details</span>
                    <div className="row  mb-1  row_div pt-4 px-4">
                      <div className="form-group col-sm-6">
                        {/* <div className="form-group"> */}
                          <label className="">
                            Select Country<span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            value={selectedCountry?.countryName || ""}
                            onChange={handleCountryChange}
                            required
                          >
                            <option value="">Select Country</option>
                            {countryList.map((country) => (
                              <option key={country.id} value={country.countryName}>
                                {country.countryName}
                              </option>
                            ))}
                          </select>
                        {/* </div> */}
                      </div>

                      <div className="form-group col-sm-6">
                        {/* <div className="form-group"> */}
                          <label className="">
                            Select Operator<span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            value={selectedOperator?.operatorName || ""}
                            onChange={handleOperatorChange}
                            required
                            disabled={!selectedCountry}
                          >
                            <option value="">Select Operator</option>
                            {selectedCountry?.operatorList?.map((operator) => (
                              <option key={operator.id} value={operator.operatorName}>
                                {operator.operatorName}
                              </option>
                            ))}
                          </select>
                        {/* </div> */}
                      </div>

                      <div className="form-group col-sm-6 mobdiv_config">
                        {/* <div className="form-group"> */}
                          <label className="">
                            Mobile Number<span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            {selectedCountry && selectedCountry.msisdnPrefix && (
                              <span className="input-group-text">+{selectedCountry.msisdnPrefix}</span>
                            )}
                            <input
                              type="text"
                              className="form-control"
                              placeholder={selectedCountry ? `Enter ${getRequiredLength()} digits` : "Select country first"}
                              value={phoneNumber}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                const maxLength = getRequiredLength();
                                if (maxLength && value.length <= maxLength) {
                                  setPhoneNumber(value);
                                }
                              }}
                              maxLength={getRequiredLength()}
                              required
                              disabled={!selectedCountry}
                            />
                          </div>
                        {/* </div> */}
                      </div>

                      <div className="form-group col-sm-6 radio_lines">
                        {/* <div className="form-group"> */}
                          <label className=" d-block">
                            Customer Status<span className="text-danger">*</span>
                          </label>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="status"
                              id="active"
                              checked={isActive}
                              onChange={() => setIsActive(true)}
                            />
                            <label className="form-check-label" htmlFor="active">
                              Active Service
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="status"
                              id="inactive"
                              checked={!isActive}
                              onChange={() => setIsActive(false)}
                            />
                            <label className="form-check-label" htmlFor="inactive">
                              Inactive Services(Subscription history)
                            </label>
                          </div>
                        {/* </div> */}
                      </div>


                    </div>
                    <div className="col-auto mt-3 mb-3 d-flex justify-content-end  buttons_divs">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!selectedCountry || !selectedOperator || !phoneNumber}
                      >
                        Submit
                      </button>
                    </div>
                  </div>


                </form>

                {/* Table section */}
                {subscriptionData.length > 0 ? (
                  <div className="card mt-4 p-3">
                    <div className="dnd-table-container">
                    <div className="form-group d-flex align-items-center justify-content-between gap-2 records">
                        <div className="d-flex">
                          <span>Show</span>
                          <select
                            className="form-select count_select mx-2"
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setCurrentPage(0);
                              handleSubmit(new Event('submit'));
                            }}
                            style={{ width: "auto" }}
                          >
                            {[10, 25, 50, 100].map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                          <span>entries</span>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary ref_but"
                            onClick={() => handleSubmit(new Event('submit'))}
                          >
                            Refresh &nbsp;
                            <i className="fas fa-sync ms-auto"></i>
                          </button>
                        </div>
                      </div>
                      <div className="subscription-table-container mt-4" id="table">
                        <CustomTable
                          data={subscriptionData}
                          enabledColumns={tableConfig.enabledColumns}
                          actions={tableConfig.actions}
                          onAction={handleAction}
                          currentPage={currentPage}
                          pageSize={pageSize}
                          onPageChange={(page) => setCurrentPage(page)}
                          showPagination={true}
                          totalElements={paginationData.totalElements}
                          totalPages={paginationData.totalPages}
                          renderAction={(row) => renderActionButtons(row.actions)}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="">
                    {/* <div className="row no-gutters">
                        <div className="col-md-12">
                          <div className="card-body p-2">
                            <p className="card_text mb-0 text-center">
                              No record found for this customer.
                            </p>
                          </div>
                        </div>
                      </div> */}
                  </div>

                )}
              </div>
            </div>
          </div>
        </div>
        <CustomModal
          show={viewCpModal}
          onHide={() => {
            setViewCpModal(false);
          }}
          title="View Customer Care Detail"
          size="xl"
          dialogClassName="custom-modal-class"
        >
          <div style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}>
            <ViewCustomerCare viewData={rowData} />
          </div>
        </CustomModal>
      </section>
    </div>
  );
}
